import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { gql, useApolloClient } from '@apollo/client';
import { AuthContextType } from '../types';
import { getFullPath } from '../utils/pathUtils';
import { forgetSignedInUser } from './signedInState';
import { GET_USER_PROFILE } from '../graphql';

const LOGOUT = gql`
  mutation Logout {
    logout
  }
`;

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const client = useApolloClient();

  useEffect(() => {
    const leftover = localStorage.getItem('token');
    if (leftover) localStorage.removeItem('token');
    client
      .query({
        query: GET_USER_PROFILE,
        fetchPolicy: 'network-only',
        context: leftover ? { headers: { authorization: `Bearer ${leftover}` } } : {},
      })
      .then(({ data }) => setIsAuthenticated(Boolean(data?.me?.id)))
      .catch(() => setIsAuthenticated(false))
      .finally(() => setIsLoading(false));
  }, [client]);

  const login = () => {
    setIsAuthenticated(true);
  };

  /**
   * Sign out, then load the sign-in page afresh. A full page load every time:
   * flipping the auth flag first sends the router to /login inside this page,
   * and everything held in memory (the open site, query results) then carried
   * over to whoever signed in next — they saw the last person's site in the
   * header until a refresh (backlog a81cf711).
   */
  const logout = (message: string | null = null) => {
    const leave = () => {
      forgetSignedInUser();
      if (message) {
        sessionStorage.setItem('authError', message);
      }
      window.location.replace(getFullPath('/login'));
    };
    client
      .mutate({ mutation: LOGOUT })
      .catch(() => undefined)
      .then(() => client.clearStore())
      .then(leave, leave);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
